import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { TextInput } from "@mantine/core";
import { Endpoints } from "../../../lib/endpoints";
import { PerformRequest } from "../../../lib/usePerformRequest";
import { InlineLoader } from "../../../components/Loader/LoadingOverlay";
import { ButtonLoader } from "../../../components/Loader";

interface UserProfile {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  phone_number?: string;
  nba_branch?: string;
  bank_name?: string;
  account_number?: string;
  account_name?: string;
  is_active: boolean;
  is_staff: boolean;
  is_lawyer: boolean;
  date_joined: string;
}

interface ProfileError {
  first_name?: string[] | string;
  last_name?: string[] | string;
  email?: string[] | string;
  phone_number?: string[] | string;
  nba_branch?: string[] | string;
  bank_name?: string[] | string;
  account_number?: string[] | string;
  account_name?: string[] | string;
  error?: string;
  message?: string;
  [key: string]: any;
}

export default function Profile() {
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const form = useForm({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      mobileNumber: "",
      nbaBranch: "",
      bank: "",
      accountNumber: "",
      accountName: "",
    },
    validate: {
      firstName: (value: string) => {
        if (!value.trim()) {
          return "First name is required";
        }
        return null;
      },
      lastName: (value: string) => {
        if (!value.trim()) {
          return "Last name is required";
        }
        return null;
      },
      email: (value: string) => {
        if (!value.trim()) {
          return "Email is required";
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
          return "Please enter a valid email address";
        }
        return null;
      },
      mobileNumber: (value: string) => {
        if (value && !/^\+?[\d\s-()]+$/.test(value.trim())) {
          return "Please enter a valid phone number";
        }
        return null;
      },
      accountNumber: (value: string) => {
        if (value && !/^\d+$/.test(value.trim())) {
          return "Account number must contain only digits";
        }
        return null;
      },
    },
  });

  useEffect(() => {
    async function fetchProfile() {
      setIsLoading(true);
      try {
        const endpoint = Endpoints.GetCurrentUser();
        const response = await PerformRequest<UserProfile>({
          method: endpoint.method,
          route: endpoint.route,
        });

        if (response.status === 200 && response.data) {
          const user = response.data;
          form.setValues({
            firstName: user.first_name || "",
            lastName: user.last_name || "",
            email: user.email || "",
            mobileNumber: user.phone_number || "",
            nbaBranch: user.nba_branch || "",
            bank: user.bank_name || "",
            accountNumber: user.account_number || "",
            accountName: user.account_name || "",
          });
        } else {
          notifications.show({
            title: "Error",
            message: "Failed to load profile. Please try again.",
            color: "red",
          });
        }
      } catch (error: any) {
        notifications.show({
          title: "Error",
          message: "Failed to load profile. Please try again.",
          color: "red",
        });
      } finally {
        setIsLoading(false);
      }
    }

    fetchProfile();
  }, []);

  async function onSubmit(values: typeof form.values) {
    setIsSubmitting(true);

    try {
      const endpoint = Endpoints.UpdateProfile();
      const response = await PerformRequest<UserProfile>({
        method: endpoint.method,
        route: endpoint.route,
        data: {
          first_name: values.firstName.trim(),
          last_name: values.lastName.trim(),
          email: values.email.trim(),
          phone_number: values.mobileNumber.trim() || undefined,
          nba_branch: values.nbaBranch.trim() || undefined,
          bank_name: values.bank.trim() || undefined,
          account_number: values.accountNumber.trim() || undefined,
          account_name: values.accountName.trim() || undefined,
        },
      });

      if (response.status === 200 && response.data) {
        notifications.show({
          title: "Success",
          message: "Profile updated successfully!",
          color: "green",
        });
      } else {
        const errorData = (response.data || {}) as ProfileError;
        let errorMessage = "Failed to update profile. Please try again.";

        // Handle field-specific errors
        const fieldErrors = [
          "first_name",
          "last_name",
          "email",
          "phone_number",
          "nba_branch",
          "bank_name",
          "account_number",
          "account_name",
        ];

        for (const field of fieldErrors) {
          if (errorData[field]) {
            if (Array.isArray(errorData[field])) {
              errorMessage = (errorData[field] as string[])[0];
            } else {
              errorMessage = errorData[field] as string;
            }
            break;
          }
        }

        if (errorData?.error) {
          errorMessage = errorData.error;
        } else if (errorData?.message) {
          errorMessage = errorData.message;
        }

        notifications.show({
          title: "Update Failed",
          message: errorMessage,
          color: "red",
        });
      }
    } catch (error: any) {
      let errorMessage = "An error occurred. Please try again.";

      if (error?.response?.data) {
        const errorData = error.response.data as ProfileError;
        const fieldErrors = [
          "first_name",
          "last_name",
          "email",
          "phone_number",
          "nba_branch",
          "bank_name",
          "account_number",
          "account_name",
        ];

        for (const field of fieldErrors) {
          if (errorData[field] && Array.isArray(errorData[field])) {
            errorMessage = (errorData[field] as string[])[0];
            break;
          }
        }

        if (errorData?.error) {
          errorMessage = errorData.error;
        } else if (errorData?.message) {
          errorMessage = errorData.message;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      notifications.show({
        title: "Update Failed",
        message: errorMessage,
        color: "red",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isLoading) {
    return (
      <div className="dashboard-page">
        <div className="dashboard-page__header">
          <h1 className="dashboard-page__title">Profile</h1>
        </div>
        <InlineLoader isLoading={true} text="Loading profile..." />
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-page__header">
        <div className="dashboard-page__header-left">
          <h1 className="dashboard-page__title">Personal Information</h1>
          <p className="dashboard-page__subtitle">
            Update your account's profile information and email address.
          </p>
        </div>
        <div className="dashboard-page__header-actions">
          <Link
            to="/d/kyc/step1"
            className="dashboard-button dashboard-button--primary"
          >
            Complete KYC
          </Link>
          <Link
            to="/d/deals"
            className="dashboard-button dashboard-button--secondary"
          >
            MY DEALS
          </Link>
        </div>
      </div>

      <div className="dashboard-profile-avatar">
        <div className="dashboard-profile-avatar__circle" />
      </div>

      <section className="dashboard-panel shadow-[var(--card-shadow)] rounded-2xl">
        <div className="dashboard-panel__header">
          <h2 className="dashboard-panel__title">Profile Details</h2>
        </div>
        <form
          className="dashboard-form"
          onSubmit={form.onSubmit(onSubmit)}
        >
          <div className="dashboard-form__grid">
            <TextInput
              label="FIRST NAME"
              disabled={isSubmitting}
              error={form.errors.firstName}
              {...form.getInputProps("firstName")}
            />
            <TextInput
              label="LAST NAME"
              disabled={isSubmitting}
              error={form.errors.lastName}
              {...form.getInputProps("lastName")}
            />
          </div>

          <div className="dashboard-form__grid">
            <TextInput
              label="MOBILE NUMBER"
              disabled={isSubmitting}
              error={form.errors.mobileNumber}
              {...form.getInputProps("mobileNumber")}
            />
            <TextInput
              label="EMAIL ADDRESS"
              type="email"
              disabled={isSubmitting}
              error={form.errors.email}
              {...form.getInputProps("email")}
            />
          </div>

          <div className="dashboard-form__grid">
            <TextInput
              label="NBA BRANCH"
              disabled={isSubmitting}
              {...form.getInputProps("nbaBranch")}
            />
            <TextInput
              label="BANK"
              disabled={isSubmitting}
              {...form.getInputProps("bank")}
            />
          </div>

          <div className="dashboard-form__grid">
            <TextInput
              label="ACCOUNT NUMBER"
              disabled={isSubmitting}
              error={form.errors.accountNumber}
              {...form.getInputProps("accountNumber")}
            />
            <TextInput
              label="ACCOUNT NAME"
              disabled={isSubmitting}
              {...form.getInputProps("accountName")}
            />
          </div>

          <div className="dashboard-form__actions">
            <button
              className="dashboard-button dashboard-button--full-width transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-2"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <ButtonLoader color="white" />
                  <span>Saving...</span>
                </>
              ) : (
                "SAVE CHANGES"
              )}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
